type EasingFunction = (t: number, b: number, c: number, d: number) => number

function easeInOutQuad(t: number, b: number, c: number, d: number): number {
  if ((t /= d / 2) < 1) return (c / 2) * t * t + b
  return (-c / 2) * (--t * (t - 2) - 1) + b
}

function easeOutQuad(t: number, b: number, c: number, d: number): number {
  return -c * (t /= d) * (t - 2) + b
}

function easeOutExpo(t: number, b: number, c: number, d: number): number {
  return t == d ? b + c : c * (-Math.pow(2, (-10 * t) / d) + 1) + b
}

function easeOutCubic(t: number, b: number, c: number, d: number): number {
  return c * ((t = t / d - 1) * t * t + 1) + b
}

function easeOutQuart(t: number, b: number, c: number, d: number): number {
  return -c * ((t = t / d - 1) * t * t * t - 1) + b
}

export function getPosition(element: HTMLElement): number {
  const scrollTop =
    window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop

  return element.getBoundingClientRect().top + scrollTop
}

export default function scrollTo(
  to: number,
  duration = 300,
  element?: HTMLElement | null,
): Promise<void> {
  const wrapper = element ?? document.body
  wrapper.style.pointerEvents = 'none'
  wrapper.style.willChange = 'scroll-position'

  const start = element
    ? element.scrollTop
    : window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop

  const change = to - start
  const increment = 20
  let currentTime = 0

  return new Promise((resolve) => {
    const animateScroll = () => {
      currentTime += increment

      const val = easeInOutQuad(currentTime, start, change, duration)
      if (element) {
        element.scrollTop = val
      } else {
        window.scrollTo(0, val)
      }

      if (currentTime < duration) {
        setTimeout(animateScroll, increment)
      } else {
        wrapper.style.pointerEvents = 'auto'
        wrapper.style.willChange = 'auto'
        resolve()
      }
    }

    animateScroll()
  })
}

const EASINGS: Record<string, EasingFunction> = {
  easeInOutQuad,
  easeOutQuad,
  easeOutExpo,
  easeOutCubic,
  easeOutQuart,
}

interface ScrollLeftOptions {
  to: number
  duration?: number
  element: HTMLElement | null
  easing?: string
}

export function scrollLeft({
  to,
  duration = 300,
  element,
  easing = 'easeOutQuart',
}: ScrollLeftOptions): Promise<void | undefined> {
  if (!element) return Promise.resolve()
  element.style.pointerEvents = 'none'
  element.style.willChange = 'scroll-position'

  const start = element.scrollLeft

  const change = to - start
  const increment = 20
  let currentTime = 0
  const ease = EASINGS[easing] || EASINGS.easeOutQuart

  return new Promise((resolve) => {
    const animateScroll = () => {
      currentTime += increment

      const val = ease(currentTime, start, change, duration)
      element.scrollLeft = val

      if (currentTime < duration) {
        setTimeout(animateScroll, increment)
      } else {
        element.style.pointerEvents = 'auto'
        element.style.willChange = 'auto'
        resolve()
      }
    }

    animateScroll()
  })
}
